import { useMcpTool } from 'webmcp-react'
import { uiStore } from 'shared/store/uiStore'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { ConversationsApi } from 'entities/Conversation'
import { MessagesApi } from 'entities/Message'
import { ConversationRoutes } from 'entities/ExportRouters'
import {
  listUnreadConversationsSchema,
  replyToConversationSchema,
} from 'entities/WebMcp/model/schemas'
import { serializeConversationSummary } from 'entities/WebMcp/lib/serializers'
import { okResult, errorResult } from 'entities/WebMcp/lib/toolResult'

/**
 * Registers the conversation WebMCP tools. Renders nothing.
 * list_unread_conversations is read-only; reply_to_conversation is a write
 * (gated) that posts into an existing thread and then opens it.
 */
export const ConversationsTools = () => {
  const { inboxesStore } = useEntitiesContext()

  useMcpTool({
    name: 'list_unread_conversations',
    description:
      'List unread conversations in the inbox the user is currently viewing. Returns conversation id, contact, unread count, and the last message. Use a conversation id with reply_to_conversation.',
    input: listUnreadConversationsSchema,
    annotations: { readOnlyHint: true },
    handler: async () => {
      try {
        // Mirror what the conversation list sends (ConversationListStore): team_id and
        // team_type are a pair, and grouped_response is required for the response to
        // include the flat `conversations` array.
        const { data } = await ConversationsApi.getConversations({
          team_id: inboxesStore.currentInboxTypeId || 0,
          team_type: inboxesStore.currentInboxType || 3,
          filter: 'unread',
          grouped_response: true,
          limit: 25,
        })
        return okResult({
          total: data.conversations_total_count,
          results: (data.conversations ?? []).map(serializeConversationSummary),
        })
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  useMcpTool({
    name: 'reply_to_conversation',
    description:
      'Reply to an existing conversation by id (e.g. one from list_unread_conversations). Requires conversation_id and the message text.',
    input: replyToConversationSchema,
    handler: async ({ conversation_id, message }) => {
      try {
        await MessagesApi.createMessage(conversation_id, { message, media_url: [] })
        // changeRouteViaVue crosses the Vue/React boundary so the thread opens,
        // matching send_message (plain changeRoute does not open the conversation).
        uiStore.changeRouteViaVue({ path: `/${ConversationRoutes.root}/${conversation_id}` })
        return okResult({ replied: true, conversation_id })
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  return null
}
